import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useOutletContext } from 'react-router-dom';
import { CalendarDays, Lock, ExternalLink, AlertTriangle, ChevronDown, Loader2, Sparkles } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { getClients, getSessions } from '../../lib/coaching-sessions';
import type { CoachingClient, CoachingSession, SessionAnalysis } from '../../lib/coaching-sessions';

interface OutletCtx { isUnlocked: boolean; }
const WALKTHROUGH_URL = 'https://sessionintelligence.app/walkthrough';

const SAMPLE_QUESTIONS = [
  '"What stood out most for you from our last conversation?"',
  '"What happens inside when you imagine saying no?"',
  '"Whose disappointment are you trying to avoid?"',
  '"What would protect your peace without disconnecting from the person?"',
  '"What value are you honouring when you say yes?"',
  '"What value are you dishonouring when you say yes?"',
];

interface GeneratedPlan {
  focus: string;
  openingQuestion: string;
  mainExploration: string;
  questions: string[];
  riskToAvoid: string;
  betweenSessionTask?: string;
}

function parsePlanFromText(text: string): GeneratedPlan {
  const lines = text.split('\n').filter(l => l.trim());

  const findSection = (label: string): string => {
    const idx = lines.findIndex(l => l.toLowerCase().includes(label.toLowerCase()));
    if (idx === -1) return '';
    // Collect content until next section header
    const result: string[] = [];
    for (let i = idx + 1; i < lines.length; i++) {
      if (/^#+\s|^\*\*[^*]+\*\*:/.test(lines[i])) break;
      result.push(lines[i].replace(/^[-•*]\s+/, '').trim());
    }
    return result.filter(Boolean).join(' ');
  };

  const extractList = (label: string): string[] => {
    const idx = lines.findIndex(l => l.toLowerCase().includes(label.toLowerCase()));
    if (idx === -1) return [];
    const result: string[] = [];
    for (let i = idx + 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      if (/^#+\s|^\*\*[^*]+\*\*:/.test(line)) break;
      const cleaned = line.replace(/^\d+\.\s+|^[-•*]\s+/, '').replace(/^\"|\"$/g, '').trim();
      if (cleaned) result.push(cleaned.startsWith('"') ? cleaned : `"${cleaned}"`);
    }
    return result;
  };

  // Best-effort extraction — fall back to slicing the raw text into sections
  const focus = findSection('suggested focus') || findSection('focus') || lines[1] || '';
  const opening = findSection('opening question') || '';
  const exploration = findSection('main exploration') || findSection('exploration') || '';
  const questions = extractList('question');
  const risk = findSection('risk') || findSection('avoid') || '';
  const between = findSection('between-session') || findSection('between session') || findSection('homework') || '';

  return {
    focus: focus || 'Identified coaching focus',
    openingQuestion: opening || questions[0] || '"What stood out most from our last session?"',
    mainExploration: exploration || 'Explore the client\'s key themes further.',
    questions: questions.length > 0 ? questions.slice(0, 8) : [],
    riskToAvoid: risk || 'Avoid moving too quickly into action before exploring emotional layers.',
    betweenSessionTask: between || undefined,
  };
}

function buildSessionSummary(analysis: SessionAnalysis, clientName: string): string {
  const parts: string[] = [];
  parts.push(`Client: ${clientName}`);
  parts.push(`Session overview: ${analysis.session_summary.overview}`);
  parts.push(`Main topic: ${analysis.session_summary.main_topic}`);
  parts.push(`Client goal: ${analysis.session_summary.client_goal}`);

  if (analysis.breakthrough_zone) {
    parts.push(`Breakthrough zone: ${analysis.breakthrough_zone.area_of_deepest_leverage}`);
    parts.push(`Reasoning: ${analysis.breakthrough_zone.reasoning}`);
  }

  if (analysis.limiting_beliefs?.length) {
    const quotes = analysis.limiting_beliefs.slice(0, 3).map(b => `"${b.client_quote}"`).join(', ');
    parts.push(`Limiting beliefs (evidence): ${quotes}`);
  }

  if (analysis.key_themes?.length) {
    const themes = analysis.key_themes.slice(0, 3).map(t => t.theme).join(', ');
    parts.push(`Key themes: ${themes}`);
  }

  if (analysis.next_session_prep) {
    parts.push(`Suggested focus from analysis: ${analysis.next_session_prep.suggested_focus}`);
    if (analysis.next_session_prep.risks_to_avoid?.length) {
      parts.push(`Risks to avoid: ${analysis.next_session_prep.risks_to_avoid.join(', ')}`);
    }
  }

  if (analysis.values_and_motivation_clues?.length) {
    const vals = analysis.values_and_motivation_clues.slice(0, 2).map(v => v.value_or_motivation).join(', ');
    parts.push(`Core values/motivations: ${vals}`);
  }

  if (analysis.session_summary.follow_up_items?.length) {
    parts.push(`Follow-up items: ${analysis.session_summary.follow_up_items.join(', ')}`);
  }

  return parts.join('\n');
}

export function SINextSessionPlannerPage() {
  const { isUnlocked } = useOutletContext<OutletCtx>();

  const [clients, setClients] = useState<CoachingClient[]>([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [sessions, setSessions] = useState<CoachingSession[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [generating, setGenerating] = useState(false);
  const [plan, setPlan] = useState<GeneratedPlan | null>(null);
  const [error, setError] = useState('');
  const [coachId, setCoachId] = useState('');

  useEffect(() => {
    if (!isUnlocked) return;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: coach } = await supabase.from('coaches').select('id').eq('user_id', user.id).maybeSingle();
      if (!coach) return;
      setCoachId(coach.id);
      const clientList = await getClients(coach.id);
      setClients(clientList);
    })();
  }, [isUnlocked]);

  useEffect(() => {
    if (!selectedClientId || !coachId) return;
    setPlan(null);
    setSelectedSessionId('');
    (async () => {
      const all = await getSessions(coachId, selectedClientId);
      setSessions(all.filter(s => s.analysis_status === 'complete' && s.session_analysis));
    })();
  }, [selectedClientId, coachId]);

  const selectedClient = clients.find(c => c.id === selectedClientId);
  const selectedSession = sessions.find(s => s.id === selectedSessionId);

  const handleGenerate = async () => {
    if (!selectedSession?.session_analysis || !selectedClient) return;
    setGenerating(true);
    setError('');
    setPlan(null);

    try {
      const sessionSummary = buildSessionSummary(selectedSession.session_analysis, selectedClient.full_name);
      const prompt = `Please generate a detailed next-session plan for ${selectedClient.full_name} based on the session analysis below.

Include:
1. **Suggested Focus** — the core theme to explore
2. **Opening Question** — the single best question to open with
3. **Main Exploration** — the key territory to move through
4. **Questions** — 6-8 targeted coaching questions (numbered list)
5. **Risk to Avoid** — what not to do too quickly
6. **Between-Session Task** — an optional reflection or homework prompt

Session Analysis:
${sessionSummary}`;

      const { data: fnData, error: fnError } = await supabase.functions.invoke('coach-intelligence-ai', {
        body: {
          messages: [{ role: 'coach', content: prompt }],
          isDemo: false,
          clientName: selectedClient.full_name,
          sessionSummary,
          contextType: 'next-session-planning',
        },
      });

      if (fnError) throw new Error(fnError.message);
      if (fnData?.error) throw new Error(fnData.error);

      const parsed = parsePlanFromText(fnData.reply || '');
      setPlan(parsed);
    } catch (e) {
      setError((e as Error).message || 'Failed to generate plan.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-6">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-2xl font-bold text-gray-80">Next Session Planner</h1>
        <p className="text-sm text-gray-50 mt-1">
          Turn client language patterns into a practical next-session plan — entering conversations with clearer focus and better questions.
        </p>
      </motion.div>

      {!isUnlocked && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white rounded-2xl border border-gray-20 p-6 flex items-start gap-4"
        >
          <div className="p-3 bg-gray-10 rounded-xl flex-shrink-0">
            <Lock className="w-5 h-5 text-gray-40" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-80 mb-1">Prepare Your Next Session With More Precision</h2>
            <p className="text-sm text-gray-50 mb-4 leading-relaxed">
              Session Intelligence™ turns client language patterns into a practical next-session plan, helping you enter the next conversation with clearer focus and better questions.
            </p>
            <a
              href={WALKTHROUGH_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-success-40 hover:bg-success-50 text-white rounded-xl text-sm font-semibold transition-colors"
            >
              Unlock Next Session Planner on the Walkthrough
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </motion.div>
      )}

      {isUnlocked ? (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white rounded-2xl border border-gray-20 p-5 space-y-4"
        >
          <h2 className="text-sm font-semibold text-gray-80">Generate a Next-Session Plan</h2>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-50 mb-1.5">Select Client</label>
              <div className="relative">
                <select
                  value={selectedClientId}
                  onChange={e => setSelectedClientId(e.target.value)}
                  className="w-full appearance-none bg-gray-5 border border-gray-20 rounded-xl px-3 py-2.5 text-sm text-gray-70 pr-8 focus:outline-none focus:ring-2 focus:ring-success-30"
                >
                  <option value="">-- Choose a client --</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.full_name}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-40 pointer-events-none" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-50 mb-1.5">Select Session</label>
              <div className="relative">
                <select
                  value={selectedSessionId}
                  onChange={e => setSelectedSessionId(e.target.value)}
                  disabled={!selectedClientId}
                  className="w-full appearance-none bg-gray-5 border border-gray-20 rounded-xl px-3 py-2.5 text-sm text-gray-70 pr-8 focus:outline-none focus:ring-2 focus:ring-success-30 disabled:opacity-50"
                >
                  <option value="">-- Choose a session --</option>
                  {sessions.map(s => (
                    <option key={s.id} value={s.id}>
                      {new Date(s.session_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      {s.notes ? ` — ${s.notes.slice(0, 40)}` : ''}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-40 pointer-events-none" />
              </div>
              {selectedClientId && sessions.length === 0 && (
                <p className="text-xs text-gray-40 mt-1">No analysed sessions yet for this client.</p>
              )}
            </div>
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-xl text-xs text-red-700">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          <button
            onClick={handleGenerate}
            disabled={!selectedSessionId || generating}
            className="flex items-center gap-2 px-5 py-2.5 bg-success-40 hover:bg-success-50 disabled:opacity-50 text-white rounded-xl text-sm font-semibold transition-colors"
          >
            {generating ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Generating Plan…</>
            ) : (
              <><Sparkles className="w-4 h-4" /> Generate Plan</>
            )}
          </button>
        </motion.div>
      ) : null}

      {/* Generated plan (unlocked) */}
      {isUnlocked && plan && (
        <motion.div
          key="generated-plan"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border border-gray-20 overflow-hidden"
        >
          <div className="px-5 py-4 border-b border-gray-10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-success-40 flex items-center justify-center text-white text-xs font-bold">
                {selectedClient?.full_name.slice(0, 2).toUpperCase()}
              </div>
              <div>
                <p className="text-xs text-gray-40 font-medium">{selectedClient?.full_name}</p>
                <h3 className="text-sm font-bold text-gray-80">Next Session Plan</h3>
              </div>
            </div>
          </div>
          <div className="p-5 space-y-5">
            <div>
              <p className="text-xs font-semibold text-gray-50 uppercase tracking-wide mb-1.5">Suggested Focus</p>
              <p className="text-base font-semibold text-gray-80">{plan.focus}</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <p className="text-xs font-semibold text-gray-50 uppercase tracking-wide mb-1.5">Opening Question</p>
                <div className="bg-success-5 border border-success-10 rounded-xl p-3">
                  <p className="text-sm text-gray-70 italic">{plan.openingQuestion}</p>
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-50 uppercase tracking-wide mb-1.5">Main Exploration</p>
                <p className="text-sm text-gray-60 leading-relaxed">{plan.mainExploration}</p>
              </div>
            </div>
            {plan.questions.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-50 uppercase tracking-wide mb-2">Coaching Questions</p>
                <ul className="space-y-2">
                  {plan.questions.map((q, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <div className="w-5 h-5 rounded-full bg-success-10 flex items-center justify-center text-xs text-success-50 font-bold flex-shrink-0 mt-0.5">{i + 1}</div>
                      <p className="text-sm text-gray-60 italic">{q}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {plan.riskToAvoid && (
              <div className="bg-orange-10 rounded-xl p-4 flex items-start gap-3">
                <AlertTriangle className="w-4 h-4 text-orange-50 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-semibold text-orange-70 mb-0.5">Risk to Avoid</p>
                  <p className="text-xs text-gray-60 leading-relaxed">{plan.riskToAvoid}</p>
                </div>
              </div>
            )}
            {plan.betweenSessionTask && (
              <div>
                <p className="text-xs font-semibold text-gray-50 uppercase tracking-wide mb-1.5">Between-Session Task</p>
                <p className="text-sm text-gray-60 italic leading-relaxed">{plan.betweenSessionTask}</p>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Demo plan — always visible when locked */}
      {!isUnlocked && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl border border-gray-20 overflow-hidden"
        >
          <div className="px-5 py-4 border-b border-gray-10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-success-40 flex items-center justify-center text-white text-xs font-bold">SA</div>
              <div>
                <p className="text-xs text-gray-40 font-medium">Sample Client</p>
                <h3 className="text-sm font-bold text-gray-80">Sarah — Next Session Plan</h3>
              </div>
            </div>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-orange-10 text-orange-70 text-xs font-semibold">Demo Preview</span>
          </div>
          <div className="p-5 space-y-5">
            <div>
              <p className="text-xs font-semibold text-gray-50 uppercase tracking-wide mb-1.5">Suggested Focus</p>
              <p className="text-base font-semibold text-gray-80">Boundaries, guilt, and emotional responsibility</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <p className="text-xs font-semibold text-gray-50 uppercase tracking-wide mb-1.5">Opening Question</p>
                <div className="bg-success-5 border border-success-10 rounded-xl p-3">
                  <p className="text-sm text-gray-70 italic">"What stood out most for you from our last conversation?"</p>
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-50 uppercase tracking-wide mb-1.5">Main Exploration</p>
                <p className="text-sm text-gray-60 leading-relaxed">The tension between wanting peace and needing approval.</p>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-50 uppercase tracking-wide mb-2">Possible Coach Questions</p>
              <ul className="space-y-2">
                {SAMPLE_QUESTIONS.map((q, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <div className="w-5 h-5 rounded-full bg-success-10 flex items-center justify-center text-xs text-success-50 font-bold flex-shrink-0 mt-0.5">{i + 1}</div>
                    <p className="text-sm text-gray-60 italic">{q}</p>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-orange-10 rounded-xl p-4 flex items-start gap-3">
              <AlertTriangle className="w-4 h-4 text-orange-50 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-orange-70 mb-0.5">Risk to Avoid</p>
                <p className="text-xs text-gray-60 leading-relaxed">
                  Do not move too quickly into action steps before exploring the emotional cost of setting boundaries.
                </p>
              </div>
            </div>
            <p className="text-xs text-gray-40 italic">This is a sample next-session plan. Your own plans will be generated from real session analyses after unlock.</p>
          </div>
        </motion.div>
      )}

      {/* What gets generated */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="bg-white rounded-2xl border border-gray-20 p-5"
      >
        <div className="flex items-center gap-2 mb-3">
          <CalendarDays className="w-4 h-4 text-success-50" />
          <h3 className="text-sm font-semibold text-gray-80">What the Planner Generates</h3>
        </div>
        <ul className="grid sm:grid-cols-2 gap-2">
          {[
            'Suggested session focus',
            'Opening question',
            'Top 5–10 exploration questions',
            'Possible intervention direction',
            'What not to push too quickly',
            'Between-session reflection or action',
            'Follow-up items',
          ].map(item => (
            <li key={item} className="flex items-center gap-2 text-sm text-gray-60">
              <div className="w-1.5 h-1.5 rounded-full bg-success-40 flex-shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      </motion.div>
    </div>
  );
}
